/**
 * State 1 of 9 — skeleton loading (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by:** no API response. It is the in-flight window of any request, and in
 * the shell specifically the window while `GET /api/v1/auth/me` is outstanding.
 *
 * It renders the *shape* of what is arriving and never a guess at its content. In
 * the sidebar that distinction is the whole point: four unlabelled bars are honest,
 * four plausible menu items are a promise the response may not keep, and a menu
 * that appears and then loses an item reads as a permission being taken away.
 *
 * `aria-busy` plus a visually hidden label, so a screen reader is told "loading"
 * once rather than reading a row of empty boxes.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';

export interface SkeletonProps {
  readonly rows?: number;
  /** Overrides the default "Đang tải" announcement with something screen-specific. */
  readonly label?: string;
  readonly variant?: 'text' | 'block';
  readonly testId?: string;
}

export function Skeleton({
  rows = 3,
  label,
  variant = 'text',
  testId = 'state-skeleton',
}: SkeletonProps): ReactNode {
  const t = useT();
  const count = Math.max(1, rows);
  return (
    <div className="wp-skeleton" aria-busy="true" role="status" data-testid={testId}>
      <span className="wp-visually-hidden">{label ?? t('state.skeleton.label')}</span>
      {Array.from({ length: count }, (_unused, index) => (
        <span
          key={index}
          className={
            variant === 'block' ? 'wp-skeleton__bar wp-skeleton__bar--tall' : 'wp-skeleton__bar'
          }
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
